// Arrastar um horário da agenda para outro dia, outra parceira ou outro horário.
// Mouse: arrasta direto. Toque: segura um instante e arrasta (rolar a tela continua normal até levantar o horário).
import { useRef, useState } from 'react';

export interface DragPos { day: string; pro: string; m: number }
export interface Dragging extends DragPos { id: string; dur: number }

export function useEventDrag<T extends { id: string }>(cfg: {
  canDrag: (a: T) => boolean;
  from: (a: T) => DragPos & { dur: number };
  // converte a posição do ponteiro em dia, parceira e minuto; `grab` é o quanto o ponteiro estava abaixo do topo do horário
  place: (clientX: number, clientY: number, grab: number, dur: number) => DragPos | null;
  drop: (a: T, to: DragPos) => void;
}) {
  const [drag, setDrag] = useState<Dragging | null>(null);
  const suppressClick = useRef(false);

  const start = (e: React.PointerEvent<HTMLElement>, a: T) => {
    if (!cfg.canDrag(a) || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const from = cfg.from(a);
    const r = e.currentTarget.getBoundingClientRect();
    const d = { type: e.pointerType, x: e.clientX, y: e.clientY, grab: e.clientY - r.top, lifted: false, timer: undefined as number | undefined, to: { day: from.day, pro: from.pro, m: from.m } as DragPos };
    let last = { x: e.clientX, y: e.clientY };
    let scroller: number | undefined;
    const place = (cx: number, cy: number) => {
      const p = cfg.place(cx, cy, d.grab, from.dur);
      if (!p) return;
      d.to = p;
      setDrag({ id: a.id, ...p, dur: from.dur });
    };
    const lift = () => { d.lifted = true; navigator.vibrate?.(12); place(last.x, last.y); };
    const move = (ev: PointerEvent) => {
      last = { x: ev.clientX, y: ev.clientY };
      if (!d.lifted) {
        const far = Math.hypot(ev.clientX - d.x, ev.clientY - d.y);
        if (d.type === 'mouse') { if (far < 5) return; lift(); }
        else { if (far > 10) end(false); return; } // mexeu antes de segurar: é rolagem da tela
      }
      ev.preventDefault();
      place(ev.clientX, ev.clientY);
    };
    const block = (ev: TouchEvent) => { if (d.lifted) ev.preventDefault(); }; // depois de levantar, o dedo não rola a tela
    const up = () => end(true);
    const cancel = () => end(false);
    const end = (commit: boolean) => {
      window.clearTimeout(d.timer);
      window.clearInterval(scroller);
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', cancel);
      document.removeEventListener('touchmove', block);
      if (!d.lifted) return;
      suppressClick.current = true;
      window.setTimeout(() => { suppressClick.current = false; }, 350);
      // o navegador ainda dispara um clique ao soltar (na coluna ou no dia vazio): engole, senão abriria um novo agendamento
      const swallow = (ev: MouseEvent) => { ev.stopPropagation(); ev.preventDefault(); };
      window.addEventListener('click', swallow, { capture: true, once: true });
      window.setTimeout(() => window.removeEventListener('click', swallow, true), 400);
      setDrag(null);
      if (commit && (d.to.day !== from.day || d.to.pro !== from.pro || d.to.m !== from.m)) cfg.drop(a, d.to);
    };
    document.addEventListener('pointermove', move, { passive: false });
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', cancel);
    document.addEventListener('touchmove', block, { passive: false });
    if (d.type !== 'mouse') d.timer = window.setTimeout(lift, 320);
    // perto da borda da tela, a agenda rola sozinha enquanto arrasta
    scroller = window.setInterval(() => {
      if (!d.lifted) return;
      const h = window.innerHeight;
      if (last.y < 110) { window.scrollBy(0, -10); place(last.x, last.y); } else if (last.y > h - 110) { window.scrollBy(0, 10); place(last.x, last.y); }
    }, 16);
  };

  return { drag, start, suppressClick };
}
